import { Fragment, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { CALC_DISCLAIMER_TEXT } from './disclaimers';

export interface MoneyProps {
  /** Integer pence. */
  pence: number | null | undefined;
  /** Prefix positive amounts with "+". */
  signed?: boolean;
  /** Drop ".00" on whole-pound amounts. */
  wholePounds?: boolean;
  /** Colour negatives with the danger tone (the minus sign still carries the meaning). */
  highlightNegative?: boolean;
  className?: string;
}

/** Formatted GBP amount from integer pence ("£1,234.56"), with tabular figures. */
export function Money({ pence, signed, wholePounds, highlightNegative, className }: MoneyProps) {
  return (
    // On phones (large text) amounts may wrap, but only after a thousands separator; never on wider screens.
    // Chromium still breaks at a <wbr> inside `nowrap` when the amount overflows its box, so the <wbr>s are
    // also removed from layout from `sm` up.
    <span className={cn('tabular-nums sm:whitespace-nowrap sm:[&_wbr]:hidden', highlightNegative && (pence ?? 0) < 0 && 'text-danger', className)}>
      {withSeparatorBreaks(formatMoney(pence, { signed, wholePounds }))}
    </span>
  );
}

/** "£1,234,567.00" → "£1,<wbr>234,<wbr>567.00" so large amounts can wrap at commas on narrow screens. */
function withSeparatorBreaks(text: string): ReactNode {
  const parts = text.split(',');
  if (parts.length === 1) return text;
  return parts.map((p, i) => (
    <Fragment key={i}>
      {i > 0 && (
        <>
          ,<wbr />
        </>
      )}
      {p}
    </Fragment>
  ));
}

export interface DateTextProps {
  /** ISO date (`2026-10-12`) or timestamp. */
  iso: string | null | undefined;
  /** Include the time ("12 Oct 2026, 14:05") for timestamps. */
  withTime?: boolean;
  className?: string;
}

/** `dd Mon yyyy` date inside a <time> element. */
export function DateText({ iso, withTime, className }: DateTextProps) {
  if (!iso) return <span className={className}>—</span>;
  return (
    <time dateTime={iso} className={cn('sm:whitespace-nowrap', className)}>
      {withTime ? formatDateTime(iso) : formatDate(iso)}
    </time>
  );
}

/** Required on every calculation screen (R-5.1). Place it directly under the figures. */
export function CalcDisclaimer({ className }: { className?: string }) {
  return (
    <p className={cn('border-l-4 border-border-strong pl-3 text-muted', className)} data-testid="calc-disclaimer">
      {CALC_DISCLAIMER_TEXT}
    </p>
  );
}
